/**
 * static-map.js
 */

import React from 'react';
import axios from 'axios';
import {
  debounce,
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/spinner';
import Icon from 'components/icon';
import { Types } from 'utils/constants';


export default function UserSearch(props) {
  const { role, value, onSelect, label: labelProp, ...rest } = props;
  const { t } = useTranslation('general');
  const [isLoading, setIsLoading] = React.useState(false);
  const [userResults, setUserResults] = React.useState([]);
  const [user, setUser] = React.useState(null);
  const label = labelProp ?? (role
    ? `Search ${Types.Role.getName(t, role)}s`
    : 'Search Users');


  React.useEffect(() => {
    setUser(value || null);
    if (!value) {
      setUserResults([]);
    }
  }, [value]);


  // search users debounced
  const searchUsers = React.useMemo(() => {
    return debounce(async (searchString) => {
      if (searchString.length < 3) {
        return;
      }
  
      setIsLoading(true);
      const response = await axios.get(`/admin/search-users?searchString=${encodeURIComponent(searchString)}&role=${role || ''}`);
      if (response.isSuccess) {
        setUserResults(response.data);
      }
      setIsLoading(false);
    }, 300);
  }, [role]);


  return (
    <Autocomplete
      {...rest}
      // freeSolo
      selectOnFocus
      blurOnSelect
      disableClearable={isLoading}
      options={userResults}
      value={user}
      filterOptions={(x) => x}
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => `${option.name}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={(e, v, r) => {
        if (r === 'input') {
          searchUsers(v);
        }
      }}
      onChange={async (e, v, r) => {
        if (r === 'clear') {
          setUserResults([]);
        }
        
        // check if selection is invalid
        if (await onSelect(v) === false) {
          setUser(null);
          setUserResults([]);
        } else {
          setUser(v);
        }
      }}
      renderInput={(params) => (
        <TextField 
          {...params} 
          size='small' 
          label={label}
          placeholder='Name or Email' 
          fullWidth 
          InputProps={{
            ...params.InputProps,
            endAdornment: isLoading ? <InputAdornment position='end' children={<Spinner size={25} />} /> : params.InputProps.endAdornment
          }}
        />
      )}
      renderOption={(props, option) => (
        <li key={option.id} {...props}>
          <Box sx={{ mr: 1 }} className='flex-hc'>
            <Icon type='handler' />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='body1' sx={{ fontWeight: 500 }}>{option.name}</Typography>
            <Typography variant='caption' sx={{ color: 'sys.onSurfaceVariant'}}>{option.email}</Typography>
            {/* <Typography variant='caption'>{option.departmentName}</Typography> */}
          </Box>
        </li>
      )}
    />
  );
}